import React, { useState, useEffect } from 'react';
import { Button } from '../../components/UI';
import { backupService } from '../../api/services';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';
import { Download, RefreshCw, Clock, Database } from 'lucide-react';

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B','KB','MB','GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
}

export default function BackupSettings() {
  const [backups,   setBackups]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [creating,  setCreating]  = useState(false);
  const [dlId,      setDlId]      = useState(null);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const r = await backupService.list();
      setBackups(r.data.data || []);
    } catch { toast.error('Failed to load backups'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBackups(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      await backupService.create();
      toast.success('Backup created successfully');
      loadBackups();
    } catch { toast.error('Failed to create backup'); }
    finally { setCreating(false); }
  };

  const downloadBackup = async (fileName) => {
    setDlId(fileName);
    try {
      const r = await backupService.download(fileName);
      const url  = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch { toast.error('Download failed'); }
    finally { setDlId(null); }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Database size={22} className="text-blue-600"/>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Database Backup</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Auto backup runs every day at 2:00 AM. Last 7 backups are kept.
              </p>
            </div>
          </div>
          <Button onClick={createBackup} loading={creating}>
            <RefreshCw size={15}/> Create Backup Now
          </Button>
        </div>

        {/* Backup List */}
        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : backups.length === 0 ? (
          <div className="text-center py-10">
            <Database size={40} className="mx-auto text-gray-300 mb-3"/>
            <p className="text-gray-600 font-medium">No backups found</p>
            <p className="text-xs text-gray-400 mt-1">No backup has been taken yet. Click "Create Backup Now" to start.</p>
            <button onClick={createBackup} disabled={creating}
              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg disabled:opacity-50">
              {creating ? 'Creating...' : '⬇️ Create First Backup'}
            </button>
          </div>
        ) : (
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">File Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Created</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Size</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {backups.map((b, i) => (
                  <tr key={b.fileName} className={`hover:bg-gray-50 ${i===0?'bg-green-50/30':''}`}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">
                      {i===0 && <span className="mr-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Latest</span>}
                      {b.fileName}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{toJalali(b.createdAt)}</td>
                    <td className="px-4 py-3 text-right text-xs text-gray-500">{formatSize(b.size)}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={()=>downloadBackup(b.fileName)} disabled={dlId===b.fileName}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium disabled:opacity-50">
                        <Download size={14}/>
                        {dlId===b.fileName ? 'Downloading...' : 'Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info box */}
        <div className="mt-5 p-4 bg-blue-50 rounded-xl border border-blue-100 flex gap-3">
          <Clock size={18} className="text-blue-500 flex-shrink-0 mt-0.5"/>
          <div>
            <p className="text-xs font-semibold text-gray-700">Automatic Daily Backup</p>
            <p className="text-xs text-gray-500 mt-1">
              System automatically creates a backup every day at 2:00 AM.
              Backups older than 7 days are automatically deleted.
            </p>
            {backups.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Latest backup: <strong>{toJalali(backups[0].createdAt)}</strong>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
